import { useEffect, useState } from 'react';
import { DropEvent, FileRejection } from 'react-dropzone';
import fileStorageApi from "../../../utilities/api/fileStorageApi";
import {AxiosProgressEvent} from "axios";

export const useFileUploadControls = (props: any) => {
  const { containerName, disabled, disablePreview, type, existingFiles, notification } = props;
  const [filePreviews, setFilePreviews] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  const [fileTypes, setFileTypes] = useState<any>({});

  useEffect(() => {
    // if we haven't set previews but there are existing file(s)

    if (filePreviews.length > 0) {
      setFilePreviews(filePreviews)
    }
    else if (!!existingFiles) {
      setFilePreviews(existingFiles)
    }

    if (props.allowedTypes === 'image') {
      setFileTypes({
        'image/jpeg': [],
        'image/png': []
      })
    } else if (props.allowedTypes === 'pdf') {
      setFileTypes({
        'application/pdf': []
      })
    } else if (props.allowedTypes === 'text') {
      setFileTypes({
        'application/msword': [], // doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [] // docx
      })
    } else if (props.allowedTypes === 'whitelist') {
      setFileTypes({
        'image/jpeg': [],
        'image/png': [],
        'text/plain': [],
        'application/pdf': [],
        'application/msword': [], // doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // docx
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
        'application/vnd.ms-excel': []
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingFiles, filePreviews, props.allowedTypes])

  // generates unique id for filename
  const { v4: uuidv4 } = require('uuid');
  type onDropEvent = (af: File[], rf: FileRejection[], e: DropEvent) => void;

  const onDropValidation = (acceptedFiles: File[], event: DropEvent) => {
    // Max request size by .NET is 28.6mb anything larger needs to throw an error
    if (acceptedFiles[0].size > 29989273) {
      return false;
    }

    return true;
  };

  /**
   * @description entry point into file upload
   * @returns
   */
  const onDrop: onDropEvent = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
    dropEvent: DropEvent
  ) => {
    if (disabled) {
      // setErrorMessage("Upload component disabled");
      return;
    }
    // Run Validation
    const valid: boolean = onDropValidation(acceptedFiles, dropEvent);
    if (!valid && notification) {
      notification('File is too large. The max file size is 28mb', 'error');
      setUploadPercentage(0);
      return;
    } else if (!valid) {
      return;
    }
    // // TODO Modify for multi-file uploads
    setFileName(acceptedFiles[0].name);
    uploadDoc(acceptedFiles);
  };

  /**
   *
   * @description handles generating uuid and uploading sending file data to api to upload in blob storage
   * this also handles notification display and calling the saveFile prop so we can store the file reference in the database
   */
  const uploadDoc = async (acceptedFiles: File[]) => {
    const formData = new FormData();
    setFileName(acceptedFiles[0].name);

    // generate unique fileId to store on blob and Attempt uploading file
    try {
      let filename: string = '';

      if (props.useOriginalFileName) {
        formData.append('file', acceptedFiles[0], acceptedFiles[0].name);
        filename = acceptedFiles[0].name;
      } else {
        let uid = uuidv4();
        // get file extension to add on uid
        let ext = acceptedFiles[0].name.split('.').pop();
        filename = `${uid}.${ext}`;
        formData.append('file', acceptedFiles[0], filename);
      }

      let data: any;

      const uploaded: any = await fileStorageApi.uploadFile(
        formData,
        (event: AxiosProgressEvent) => {
          // @ts-ignore
          setUploadPercentage(Math.round((event.loaded * 50) / event.total));

          // @ts-ignore
          if(Math.round((event.loaded) / event.total) === 1){
            setUploadPercentage(75);
          }
        },
          containerName,
          filename,
      ).then((response)=>{
        data = response
        setUploadPercentage(100);
      }).catch();

      // External save called to set DB reference with blob info
      try {
        let data: any = {
          fileName: acceptedFiles[0].name,
          blobName: filename,
          containerName: containerName,
        };
        props.saveFile(data);
      } catch (error) {
        console.error('Failed to save reference to db. Make sure you pass in the saveFile prop');
      }

      if (data.data.isSuccess) {
        notification('File uploaded successfully', 'success');
        // clone array where file preivews may inherit immutable behavior from redux
        let filesToShow = [...filePreviews];
        filesToShow.unshift({
          fileName: acceptedFiles[0].name,
          containerName: containerName,
          blobName: filename,
          id: Math.floor(Math.random() * 10000)
        });
        setFilePreviews(filesToShow);
      } else {
        notification(`${uploaded.message}`, 'error');
      }
    } catch (err: any) {
      setUploadPercentage(0);
      if (err.response?.status === 500) {
        notification(err.message, 'error');
      }
      else if (err.response?.status === 400) {
        notification(err.response.data.message.substring(0, 100) , 'error');
      }
      else {
        notification(err.message, 'error');
      }
    }
  };

  // Copies file name to users clipboard
  const copyFileUrl = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  /**
   * @description deletes the file from blob storage and removes the file from preview
   * TODO: this still needs to handle deletion of the database references if they exist a removeFile prop should also get passed into this component
   *
   */
  const removeFile = async (file: any) => {
    try {
      let response: any = await fileStorageApi.deleteFile(file.blobName, containerName);
      let fileToRemove;
      if (response.data.isSuccess) {
        // clone array where file preivews may inherit immutable behavior from redux
        let tempArray = [...filePreviews];
        for (let i = 0; i < filePreviews.length; i++) {
          if (filePreviews[i].blobName === file.blobName) {
            try {
              fileToRemove = tempArray.splice(i, 1);
              await props.deleteFile(fileToRemove);
              setFilePreviews(tempArray);
              // clean up drop zone
              setFileName('');
              setUploadPercentage(0);
            } catch (error) {
              setFilePreviews(filePreviews);
            }
          }
        }
        notification('File deleted successfully', 'success');
      } else {
        notification('File failed to delete', 'error');
      }
    } catch (error: any) {
      notification(`Failed to delete: ${error.message}`, 'error');
    }
  };

  return {
    // functions
    uploadDoc,
    onDrop,
    copyFileUrl,
    removeFile,
    // state
    filePreviews,
    setFilePreviews,
    fileName,
    uploadPercentage,
    fileTypes,
    // props
    containerName,
    disabled,
    disablePreview,
    type,
    existingFiles,
  };
};
